import { f0FormField, RenderIfCondition } from '@factorialco/f0-react'
import { F0SelectItemObject } from '@factorialco/f0-react'
import {
  PayrollValueObjectsConceptCategoryEnum,
  PayrollValueObjectsConceptUnitTypeEnum,
} from 'generated/resources/graphql'
import { z } from 'zod'

import { PayrollConceptRecord, usePayrollConceptSource } from './source'

type BasePayrollConceptSelectorFormFieldParams = {
  label: string
  section?: string
  placeholder?: string
  helpText?: string
  renderIf?: RenderIfCondition
  row?: string
  categories?: PayrollValueObjectsConceptCategoryEnum[]
  unitTypes?: PayrollValueObjectsConceptUnitTypeEnum[]
  withActiveStatus?: boolean
}

const mapPayrollConceptOptions = (
  concept: PayrollConceptRecord
): F0SelectItemObject<string, PayrollConceptRecord> => ({
  value: concept.id,
  label: concept.label,
  item: concept,
})

// Single select - optional
const useOptionalSinglePayrollConceptSelectorFormField = ({
  label,
  section,
  placeholder,
  helpText,
  renderIf,
  row,
  categories,
  unitTypes,
  withActiveStatus,
}: BasePayrollConceptSelectorFormFieldParams) => {
  const schema = z.string().optional()

  const source = usePayrollConceptSource({ categories, unitTypes, withActiveStatus })

  return f0FormField(schema, {
    label,
    section,
    fieldType: 'select',
    helpText,
    placeholder,
    renderIf,
    row,
    showSearchBox: false,
    source,
    mapOptions: mapPayrollConceptOptions,
  })
}

// Single select - mandatory
const useMandatorySinglePayrollConceptSelectorFormField = ({
  label,
  section,
  placeholder,
  helpText,
  renderIf,
  row,
  categories,
  unitTypes,
  withActiveStatus,
}: BasePayrollConceptSelectorFormFieldParams) => {
  const schema = z.string().min(1)

  const source = usePayrollConceptSource({ categories, unitTypes, withActiveStatus })

  return f0FormField(schema, {
    label,
    section,
    fieldType: 'select',
    helpText,
    placeholder,
    renderIf,
    row,
    showSearchBox: false,
    source,
    mapOptions: mapPayrollConceptOptions,
  })
}

// Multi select - optional
const useOptionalMultiPayrollConceptSelectorFormField = ({
  label,
  section,
  placeholder,
  helpText,
  renderIf,
  row,
  categories,
  unitTypes,
  withActiveStatus,
}: BasePayrollConceptSelectorFormFieldParams) => {
  const schema = z.array(z.string()).optional()

  const source = usePayrollConceptSource({ categories, unitTypes, withActiveStatus })

  return f0FormField(schema, {
    label,
    section,
    fieldType: 'select',
    helpText,
    placeholder,
    renderIf,
    row,
    multiple: true,
    showSearchBox: false,
    source,
    mapOptions: mapPayrollConceptOptions,
  })
}

// Multi select - mandatory
const useMandatoryMultiPayrollConceptSelectorFormField = ({
  label,
  section,
  placeholder,
  helpText,
  renderIf,
  row,
  categories,
  unitTypes,
  withActiveStatus,
}: BasePayrollConceptSelectorFormFieldParams) => {
  const schema = z.array(z.string()).min(1)

  const source = usePayrollConceptSource({ categories, unitTypes, withActiveStatus })

  return f0FormField(schema, {
    label,
    section,
    fieldType: 'select',
    helpText,
    placeholder,
    renderIf,
    row,
    multiple: true,
    showSearchBox: false,
    source,
    mapOptions: mapPayrollConceptOptions,
  })
}

type SinglePayrollConceptSelectorFormFieldParams<M extends boolean> =
  BasePayrollConceptSelectorFormFieldParams & {
    mandatory: M
  }

export const useSinglePayrollConceptSelectorFormField = <M extends boolean>({
  mandatory,
  renderIf,
  ...rest
}: SinglePayrollConceptSelectorFormFieldParams<M>) => {
  const mandatorySinglePayrollConceptSelectorFormField =
    useMandatorySinglePayrollConceptSelectorFormField({
      ...rest,
      renderIf,
    })
  const optionalSinglePayrollConceptSelectorFormField =
    useOptionalSinglePayrollConceptSelectorFormField({
      ...rest,
      renderIf,
    })

  return mandatory
    ? mandatorySinglePayrollConceptSelectorFormField
    : optionalSinglePayrollConceptSelectorFormField
}

type MultiPayrollConceptSelectorFormFieldParams<M extends boolean> =
  BasePayrollConceptSelectorFormFieldParams & {
    mandatory: M
  }

const useMultiPayrollConceptSelectorFormField = <M extends boolean>({
  mandatory,
  renderIf,
  ...rest
}: MultiPayrollConceptSelectorFormFieldParams<M>) => {
  const mandatoryMultiPayrollConceptSelectorFormField =
    useMandatoryMultiPayrollConceptSelectorFormField({
      ...rest,
      renderIf,
    })
  const optionalMultiPayrollConceptSelectorFormField =
    useOptionalMultiPayrollConceptSelectorFormField({
      ...rest,
      renderIf,
    })

  return mandatory
    ? mandatoryMultiPayrollConceptSelectorFormField
    : optionalMultiPayrollConceptSelectorFormField
}
